import React, { useState, useEffect } from 'react';
import { projectAPI, salesEnablementAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Lightbulb, Sparkles, Target, HelpCircle, MessageCircle, 
  Plus, Trash2, Save, Building2, ChevronDown, ChevronUp,
  GripVertical, Check, X, Edit2
} from 'lucide-react';

const SalesEnablementPage = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('pitch');
  
  // Form state
  const [elevatorPitch, setElevatorPitch] = useState('');
  const [keyHighlights, setKeyHighlights] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [objectionHandlers, setObjectionHandlers] = useState([]);
  
  // New item states
  const [newHighlight, setNewHighlight] = useState('');
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  const [newObjection, setNewObjection] = useState({ objection: '', response: '' });
  
  // Edit states
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingObjection, setEditingObjection] = useState(null);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      loadSalesEnablement(selectedProjectId);
    }
  }, [selectedProjectId]);

  const loadProjects = async () => {
    try {
      const res = await projectAPI.list();
      setProjects(res.data);
      if (res.data.length > 0) {
        setSelectedProjectId(res.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadSalesEnablement = async (projectId) => {
    try {
      const res = await salesEnablementAPI.getForProject(projectId);
      const data = res.data;
      setElevatorPitch(data.elevator_pitch || '');
      setKeyHighlights(data.key_highlights || []);
      setFaqs(data.faqs || []);
      setObjectionHandlers(data.objection_handlers || []);
    } catch (error) {
      // Reset form if no data exists
      setElevatorPitch('');
      setKeyHighlights([]);
      setFaqs([]);
      setObjectionHandlers([]);
    }
  };

  const handleSave = async () => {
    if (!selectedProjectId) {
      toast.error('Please select a project');
      return;
    }

    setSaving(true);
    try {
      await salesEnablementAPI.createOrUpdate(selectedProjectId, {
        project_id: selectedProjectId,
        elevator_pitch: elevatorPitch,
        key_highlights: keyHighlights,
        faqs: faqs,
        objection_handlers: objectionHandlers
      });
      toast.success('Sales enablement saved successfully');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Highlight handlers
  const addHighlight = () => {
    if (!newHighlight.trim()) return;
    setKeyHighlights([...keyHighlights, newHighlight.trim()]);
    setNewHighlight('');
  };

  const removeHighlight = (index) => {
    setKeyHighlights(keyHighlights.filter((_, i) => i !== index));
  };

  const moveHighlight = (index, direction) => {
    const newList = [...keyHighlights];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newList.length) return;
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    setKeyHighlights(newList);
  };

  // FAQ handlers
  const addFaq = () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      toast.error('Please fill both question and answer');
      return;
    }
    setFaqs([...faqs, { ...newFaq }]);
    setNewFaq({ question: '', answer: '' });
  };

  const updateFaq = (index) => {
    if (!editingFaq.question.trim() || !editingFaq.answer.trim()) return;
    const newFaqs = [...faqs];
    newFaqs[index] = editingFaq;
    setFaqs(newFaqs);
    setEditingFaq(null);
  };

  const removeFaq = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  // Objection handlers
  const addObjectionHandler = () => {
    if (!newObjection.objection.trim() || !newObjection.response.trim()) {
      toast.error('Please fill both objection and response');
      return;
    }
    setObjectionHandlers([...objectionHandlers, { ...newObjection }]);
    setNewObjection({ objection: '', response: '' });
  };

  const updateObjectionHandler = (index) => {
    if (!editingObjection.objection.trim() || !editingObjection.response.trim()) return;
    const newList = [...objectionHandlers];
    newList[index] = editingObjection;
    setObjectionHandlers(newList);
    setEditingObjection(null);
  };

  const removeObjectionHandler = (index) => {
    setObjectionHandlers(objectionHandlers.filter((_, i) => i !== index));
  };

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="sales-enablement-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-enablement-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Lightbulb className="h-7 w-7 text-amber-600" />
            Sales Enablement
          </h1>
          <p className="text-muted-foreground">Create elevator pitches, FAQs, and objection handlers for your projects</p>
        </div>
        <Button onClick={handleSave} disabled={saving || !selectedProjectId} data-testid="save-btn">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Project Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1">
              <Label className="text-sm text-muted-foreground">Select Project</Label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger className="w-full mt-1" data-testid="project-selector">
                  <SelectValue placeholder="Choose a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedProject && (
              <Badge variant="secondary" className="mt-6">
                {selectedProject.location || 'No location'}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Content Tabs */}
      {selectedProjectId && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="pitch" data-testid="tab-pitch">
              <Sparkles className="h-4 w-4 mr-2" />
              Elevator Pitch
            </TabsTrigger>
            <TabsTrigger value="highlights" data-testid="tab-highlights">
              <Target className="h-4 w-4 mr-2" />
              Key Highlights
            </TabsTrigger>
            <TabsTrigger value="faqs" data-testid="tab-faqs">
              <HelpCircle className="h-4 w-4 mr-2" />
              FAQs
            </TabsTrigger>
            <TabsTrigger value="objections" data-testid="tab-objections">
              <MessageCircle className="h-4 w-4 mr-2" />
              Objection Handlers
            </TabsTrigger>
          </TabsList>

          {/* Elevator Pitch Tab */}
          <TabsContent value="pitch" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-indigo-600" />
                  Elevator Pitch
                </CardTitle>
                <CardDescription>
                  A 30-60 second compelling summary of the project that sales team can use on calls
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={elevatorPitch}
                  onChange={(e) => setElevatorPitch(e.target.value)}
                  placeholder="Welcome to [Project Name], where luxury meets convenience. Our premium residential development offers world-class amenities including..."
                  rows={8}
                  className="resize-none"
                  data-testid="elevator-pitch-input"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {elevatorPitch.length} characters • Recommended: 300-500 characters
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Key Highlights Tab */}
          <TabsContent value="highlights" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-emerald-600" />
                  Key Highlights
                </CardTitle>
                <CardDescription>
                  Bullet points of unique selling points and features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add new highlight */}
                <div className="flex gap-2">
                  <Input
                    value={newHighlight}
                    onChange={(e) => setNewHighlight(e.target.value)}
                    placeholder="Enter a key highlight..."
                    onKeyPress={(e) => e.key === 'Enter' && addHighlight()}
                    data-testid="new-highlight-input"
                  />
                  <Button onClick={addHighlight} data-testid="add-highlight-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Highlights list */}
                {keyHighlights.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No highlights added yet</p>
                    <p className="text-sm">Add key selling points above</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {keyHighlights.map((highlight, index) => (
                      <div 
                        key={index} 
                        className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200"
                      >
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                        <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                        <span className="flex-1 text-sm">{highlight}</span>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7"
                            onClick={() => moveHighlight(index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7"
                            onClick={() => moveHighlight(index, 'down')}
                            disabled={index === keyHighlights.length - 1}
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7 text-red-500 hover:text-red-700"
                            onClick={() => removeHighlight(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQs Tab */}
          <TabsContent value="faqs" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-blue-600" />
                  Frequently Asked Questions
                </CardTitle>
                <CardDescription>
                  Common questions and answers for quick reference during calls
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add new FAQ */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg border">
                  <h4 className="font-medium text-sm">Add New FAQ</h4>
                  <Input
                    value={newFaq.question}
                    onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                    placeholder="Question (e.g., What is the possession date?)"
                    data-testid="new-faq-question"
                  />
                  <Textarea
                    value={newFaq.answer}
                    onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                    placeholder="Answer..."
                    rows={2}
                    data-testid="new-faq-answer"
                  />
                  <Button onClick={addFaq} size="sm" data-testid="add-faq-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    Add FAQ
                  </Button>
                </div>

                {/* FAQs list */}
                {faqs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <HelpCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No FAQs added yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {faqs.map((faq, index) => (
                      <div 
                        key={index} 
                        className="p-4 border rounded-lg"
                      >
                        {editingFaq?.index === index ? (
                          <div className="space-y-3">
                            <Input
                              value={editingFaq.question}
                              onChange={(e) => setEditingFaq({ ...editingFaq, question: e.target.value })}
                            />
                            <Textarea
                              value={editingFaq.answer}
                              onChange={(e) => setEditingFaq({ ...editingFaq, answer: e.target.value })}
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => updateFaq(index)}>
                                <Check className="h-4 w-4 mr-1" />
                                Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingFaq(null)}>
                                <X className="h-4 w-4 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-medium text-blue-700">{faq.question}</p>
                                <p className="text-sm text-muted-foreground mt-1">{faq.answer}</p>
                              </div>
                              <div className="flex gap-1 ml-2">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-7 w-7"
                                  onClick={() => setEditingFaq({ ...faq, index })}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-7 w-7 text-red-500"
                                  onClick={() => removeFaq(index)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Objection Handlers Tab */}
          <TabsContent value="objections" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-amber-600" />
                  Objection Handlers
                </CardTitle>
                <CardDescription>
                  Common objections customers raise and suggested responses
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add new objection */}
                <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-sm">Add New Objection Handler</h4>
                  <Input
                    value={newObjection.objection}
                    onChange={(e) => setNewObjection({ ...newObjection, objection: e.target.value })}
                    placeholder="Customer objection (e.g., The price is too high)"
                    data-testid="new-objection-input"
                  />
                  <Textarea
                    value={newObjection.response}
                    onChange={(e) => setNewObjection({ ...newObjection, response: e.target.value })}
                    placeholder="Your suggested response..."
                    rows={2}
                    data-testid="new-response-input"
                  />
                  <Button onClick={addObjectionHandler} size="sm" data-testid="add-objection-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Handler
                  </Button>
                </div>

                {/* Objection handlers list */}
                {objectionHandlers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No objection handlers added yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {objectionHandlers.map((oh, index) => (
                      <div 
                        key={index} 
                        className="p-4 border-l-4 border-amber-500 bg-amber-50 rounded-r-lg"
                      >
                        {editingObjection?.index === index ? (
                          <div className="space-y-3">
                            <Input
                              value={editingObjection.objection}
                              onChange={(e) => setEditingObjection({ ...editingObjection, objection: e.target.value })}
                            />
                            <Textarea
                              value={editingObjection.response}
                              onChange={(e) => setEditingObjection({ ...editingObjection, response: e.target.value })}
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => updateObjectionHandler(index)}>
                                <Check className="h-4 w-4 mr-1" />
                                Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingObjection(null)}>
                                <X className="h-4 w-4 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-amber-800">"{oh.objection}"</p>
                              <p className="text-sm text-amber-700 mt-1">→ {oh.response}</p>
                            </div>
                            <div className="flex gap-1 ml-2">
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-7 w-7"
                                onClick={() => setEditingObjection({ ...oh, index })}
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-7 w-7 text-red-500"
                                onClick={() => removeObjectionHandler(index)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {!selectedProjectId && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No Project Selected</p>
            <p className="text-muted-foreground">Select a project above to manage its sales enablement content</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SalesEnablementPage;
